import {
  Stack,
  FormControl,
  FormLabel,
  Button,
  Box,
  HStack,
  Text,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  Icon,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';

import { useFieldArray, useFormContext } from 'react-hook-form';

import { useEffect, useRef, useState } from 'react';

import { FiPlusCircle, FiTrash2, FiFile, FiUploadCloud, FiEdit2, FiEye } from 'react-icons/fi';

import MessageError from '../../../../../components/MessageError';
import { formatFileSize } from '../../../../../utils/functions';
import ExistingExemptionAttachments from '../ExistingExemptionAttachments';

interface IDocument {
  file: File;
  description: string;
  role: string;
}

export interface IEconomicHyposufficiencyProps {
  type_exemption?: string;
  subtype?: string;
  documents: IDocument[];
  existingExemptionAttachments?: { id: string }[];
  removedExemptionAttachmentIds?: string[];
}

const DocumentRow: React.FC<{ document: IDocument; onEdit: () => void; onRemove: () => void }> = ({
  document,
  onEdit,
  onRemove,
}) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(document.file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [document.file]);

  return (
    <Flex
      p={3}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      boxShadow="sm"
      minW={0}
      align="center"
      gap={3}
    >
      <Text
        fontSize="sm"
        color="gray.800"
        fontWeight="bold"
        minW={0}
        maxW="35%"
        isTruncated
        title={document.description}
      >
        {document.description}
      </Text>
      <HStack spacing={1} minW={0} flex={1} borderLeft="1px solid" borderColor="gray.200" pl={4}>
        <Icon as={FiFile} boxSize={4} color="blue.600" flexShrink={0} />
        <Text
          as="a"
          href={url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          fontSize="sm"
          fontWeight="medium"
          color="blue.600"
          isTruncated
          title={document.file.name}
          minW={0}
          _hover={{ textDecoration: 'underline' }}
        >
          {document.file.name}
        </Text>
        <Text fontSize="xs" color="gray.500" flexShrink={0}>
          ({formatFileSize(document.file.size)})
        </Text>
      </HStack>
      <HStack spacing={1} flexShrink={0}>
        <IconButton
          as="a"
          href={url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visualizar ${document.file.name}`}
          title="Visualizar arquivo"
          icon={<FiEye />}
          variant="ghost"
          size="sm"
          colorScheme="blue"
        />
        <IconButton
          aria-label="Editar documento"
          icon={<FiEdit2 />}
          variant="ghost"
          size="sm"
          colorScheme="blue"
          onClick={onEdit}
        />
        <IconButton
          aria-label="Remover documento"
          icon={<FiTrash2 />}
          variant="ghost"
          size="sm"
          colorScheme="red"
          onClick={onRemove}
        />
      </HStack>
    </Flex>
  );
};

const EconomicHyposufficiency: React.FC = () => {
  const { watch, control, formState } = useFormContext<IEconomicHyposufficiencyProps>();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [modalError, setModalError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const existingAttachments = watch('existingExemptionAttachments') || [];
  const removedAttachmentIds = watch('removedExemptionAttachmentIds') || [];
  const hasExistingAttachments = existingAttachments.some(
    (attachment) => !removedAttachmentIds.includes(attachment.id),
  );

  // const subTypeExemption = useSubTypeExemption(watch('type_exemption') || '');

  const {
    fields: documents,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: 'documents',
  });

  // const mountHipoSufficiency = useMemo(() => {
  //   if (!subTypeExemption.data) {
  //     return null;
  //   }

  //   return subTypeExemption.data.map((item) => (
  //     <option key={item.id} value={item.id}>
  //       {item.name}
  //     </option>
  //   ));
  // }, [subTypeExemption.data]);

  const handleCloseModal = () => {
    setSelectedFile(null);
    setDescription('');
    setEditingIndex(null);
    setModalError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onClose();
  };

  const handleEditDocument = (index: number) => {
    const document = documents[index];
    setEditingIndex(index);
    setSelectedFile(document.file);
    setDescription(document.description);
    setModalError('');
    onOpen();
  };

  /**
   * Captura o arquivo selecionado.
   */
  const selectFile = (file?: File) => {
    if (!file) {
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(extension ?? '')) {
      setModalError('Formato não suportado. Envie PDF, JPG, JPEG ou PNG.');
      return;
    }
    if (file.size === 0 || file.size > 2 * 1024 * 1024) {
      setModalError('O arquivo deve ter conteúdo e no máximo 2 MB.');
      return;
    }
    setSelectedFile(file);
    setModalError('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
    event.target.value = '';
  };

  /**
   * Adiciona o documento à lista.
   */
  const handleAddDocument = () => {
    if (!selectedFile) {
      setModalError('Selecione um arquivo.');
      return;
    }

    if (!description.trim()) {
      setModalError('Informe a descrição do documento.');
      return;
    }
    if (description.trim().length > 150) {
      setModalError('A descrição deve ter no máximo 150 caracteres.');
      return;
    }

    const document = { file: selectedFile, description: description.trim(), role: description.trim() };
    if (editingIndex === null) {
      append(document);
    } else {
      update(editingIndex, document);
    }

    handleCloseModal();
  };

  return (
    <>
      <Stack spacing={{ base: 4, md: 5 }} w="100%" minW={0}>
        {/* SUBTIPO */}
        {/* <FormControl isRequired maxW={isMobile ? '100%' : '420px'} isInvalid={!!formState.errors?.subtype}>
          {!isMobile && <TextXS as={FormLabel}>Qual situação melhor representa sua condição?</TextXS>}

          {isMobile ? (
            <InputSelectMobile
              name="subtype"
              placeholderLabel="Situação de hipossuficiência"
              isRequired
              isExternalState
              selectedItem={subTypeExemption.data?.find((item) => item.id === watch('subtype'))}
              listItens={subTypeExemption.data?.map((item) => ({ id: item.id, name: item.name }))}
              onChangeItem={(item) => setValue('subtype', item?.id ?? '', { shouldValidate: true })}
              isDisabled={subTypeExemption.isLoading || !watch('type_exemption')}
            />
          ) : (
            <Select
              {...register('subtype', { required: 'Selecione uma situação.' })}
              isDisabled={subTypeExemption.isLoading}
            >
              <option value="">Selecione...</option>
              {mountHipoSufficiency}
            </Select>
          )}

          <MessageError>{formState.errors?.subtype?.message}</MessageError>
          {!subTypeExemption.isLoading && subTypeExemption.data?.length === 0 && (
            <Text fontSize="sm" color="orange.600" mt={2}>
              Não há situações disponíveis para este tipo de isenção. Entre em contato com a organização do processo
              seletivo.
            </Text>
          )}
          {subTypeExemption.isError && (
            <Text fontSize="sm" color="red.600" mt={2}>
              Não foi possível carregar as situações. Tente novamente mais tarde.
            </Text>
          )}
        </FormControl> */}

        <Stack spacing={3} minW={0}>
          <Flex justify="space-between" align="center" gap={3}>
            <Text fontSize="sm" fontWeight="bold" color="gray.700">
              Envio de Documentos{' '}
              <Text as="span" color="red.500">
                *
              </Text>
            </Text>
            <HStack spacing={3} flexShrink={0}>
              {!isMobile && (
                <Button
                  leftIcon={<FiPlusCircle size={20} />}
                  colorScheme="blue"
                  variant="outline"
                  borderRadius="full"
                  size="sm"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  lineHeight="1"
                  onClick={onOpen}
                >
                  Adicionar documento
                </Button>
              )}
            </HStack>
          </Flex>

          <ExistingExemptionAttachments compact>
            {documents.length === 0 && !hasExistingAttachments ? (
              <Stack
                align="center"
                justify="center"
                spacing={1}
                py={4}
                px={4}
                minH="150px"
                border="1px dashed"
                borderColor="gray.300"
                borderRadius="lg"
                textAlign="center"
              >
                <Icon as={FiFile} boxSize={7} color="gray.400" />
                <Text fontSize="sm" color="gray.500">
                  Os documentos adicionados aparecerão aqui.
                </Text>
              </Stack>
            ) : (
              <>
                {documents.map((document, index) => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    onEdit={() => handleEditDocument(index)}
                    onRemove={() => remove(index)}
                  />
                ))}
              </>
            )}
          </ExistingExemptionAttachments>
          {isMobile && (
            <Stack spacing={2} align="center">
              <Button
                leftIcon={<FiPlusCircle size={20} />}
                colorScheme="blue"
                variant="outline"
                borderRadius="full"
                size="sm"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                lineHeight="1"
                onClick={onOpen}
              >
                Adicionar documento
              </Button>
            </Stack>
          )}
          {formState.errors?.documents && <MessageError>{formState.errors?.documents?.message}</MessageError>}
        </Stack>
        {/* {watch('subtype') && (
        )} */}
      </Stack>

      {/* MODAL */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered={!isMobile} size="lg">
        <ModalOverlay />

        <ModalContent
          position={isMobile ? 'absolute' : undefined}
          bottom={isMobile ? 0 : undefined}
          w={isMobile ? '100%' : undefined}
          maxW={isMobile ? '100dvw' : undefined}
          maxH={isMobile ? '90dvh' : undefined}
          m={isMobile ? 0 : undefined}
          borderRadius={isMobile ? '18px 18px 0 0' : undefined}
          overflowY="auto"
        >
          <Box position="sticky" top={0} zIndex={2} bg="white" borderTopRadius={isMobile ? '18px' : undefined}>
            <ModalHeader fontSize="lg">
              {editingIndex === null ? 'Adicionar documento' : 'Editar documento'}
            </ModalHeader>
            <ModalCloseButton />
          </Box>

          <ModalBody pb={5}>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Descrição</FormLabel>
                <Input
                  value={description}
                  maxLength={150}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    if (modalError) setModalError('');
                  }}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Arquivo</FormLabel>

                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  hidden
                  onChange={handleFileChange}
                />
                <Stack
                  align="center"
                  justify="center"
                  spacing={1}
                  py={{ base: 5, md: 7 }}
                  px={3}
                  border="2px dashed"
                  borderColor={isDragging ? 'blue.400' : 'gray.300'}
                  bg={isDragging ? 'blue.50' : 'white'}
                  borderRadius="lg"
                  textAlign="center"
                  cursor="pointer"
                  role="button"
                  tabIndex={0}
                  aria-label="Selecionar arquivo comprobatório"
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    selectFile(event.dataTransfer.files[0]);
                  }}
                >
                  <Icon as={FiUploadCloud} boxSize={7} color="blue.500" />
                  <Stack gap={0} align="center">
                    <Text fontSize="sm" fontWeight="semibold">
                      Toque para selecionar ou arraste um arquivo
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      PDF, JPG ou PNG · até 2 MB por arquivo.
                    </Text>
                  </Stack>
                </Stack>
              </FormControl>

              {selectedFile && (
                <HStack
                  p={3}
                  border="1px solid"
                  borderColor="green.200"
                  bg="green.50"
                  borderRadius="md"
                  spacing={3}
                  minW={0}
                >
                  <Icon as={FiFile} color="green.600" boxSize={5} flexShrink={0} />
                  <Stack spacing={0} minW={0} flex={1}>
                    <Text fontSize="sm" fontWeight="semibold" isTruncated title={selectedFile.name}>
                      {selectedFile.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatFileSize(selectedFile.size)}
                    </Text>
                  </Stack>
                  <IconButton
                    aria-label="Remover arquivo selecionado"
                    icon={<FiTrash2 />}
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  />
                </HStack>
              )}

              {modalError && <MessageError>{modalError}</MessageError>}
            </Stack>
          </ModalBody>

          <ModalFooter
            position="sticky"
            bottom={0}
            zIndex={2}
            bg="white"
            borderTop="1px solid"
            borderColor="gray.100"
            py={3}
            pr={{ base: 16, md: 6 }}
          >
            <HStack spacing={3} w="full" justify="flex-end">
              <Button variant="ghost" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button colorScheme="blue" leftIcon={<FiPlusCircle />} onClick={handleAddDocument}>
                {editingIndex === null ? 'Adicionar' : 'Salvar alterações'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EconomicHyposufficiency;
